<app-legal-layout>
  <h1>Privacy Policy</h1>
  <p>Last modified: June 1, 2025</p>
  <h2>Introduction</h2>
  <p>
    Monarch Specialty Group LLC (“<b>Company</b>” or “<b>We</b>”) respect your privacy and are committed to protecting it through our
    compliance with this policy.
  </p>
  <p>
    This policy describes the types of information we may collect from you or that you may provide when you contact brightscrip, visit
    brightscrip.com or visit brightTERMINAL (our “<b>Contact Center</b>” and “<b>Website(s)</b>”, respectively) and our practices for
    collecting, using, maintaining, protecting, and disclosing that information.
  </p>
  <p>This policy applies to information we collect:</p>
  <ul>
    <li>On the Websites.</li>
    <li>In email, text, and other electronic messages between you and the Contact Center or Websites.</li>
    <li>
      Through mobile and desktop applications you download from the Websites, which provide dedicated non-browser-based interaction between
      you and the Websites.
    </li>
    <li>In the ordinary course of business.</li>
  </ul>
  <p>It does not apply to information collected by:</p>
  <ul>
    <li>
      Us offline or through any other means, including on any other website operated by Company or any third party (including our affiliates
      and subsidiaries); or
    </li>
    <li>
      Any third party (including our affiliates and subsidiaries), including through any application or content (including advertising) that
      may link to or be accessible from or through the Website.
    </li>
  </ul>
  <p>
    Please read this policy carefully to understand our policies and practices regarding your information and how we will treat it. If you
    do not agree with our policies and practices, your choice is not to interact with our Contact Center or use our Websites. By contacting
    our Contact Center or accessing or using this Website, you agree to this privacy policy. This policy may change from time to time (see
    <a (click)="jumpToElement('changes-to-our-privacy-policy')">Changes to Our Privacy Policy</a>). Your continued use of the Contact Center
    or Website after we make changes is deemed to be acceptance of those changes, so please check the policy periodically for updates.
  </p>
  <h2>Children Under the Age of 16</h2>
  <p>
    Our Websites are not intended for children under 16 years of age. No one under age 16 may provide any information to or on the Websites.
    We do not knowingly collect personal information from children under 16. If you are under 16, do not use or provide any information on
    the Websites or through any of its features or provide any information about yourself to us, including your name, address, telephone
    number, email address, or any screen name or user name you may use. If we learn we have collected or received personal information from
    a child under 16 without verification of parental consent, we will delete that information. If you believe we might have any information
    from a child under 16, please contact us at <a href="tel:833-613-2333">833-613-2333</a> or via email at
    <a href="mailto:support@brightscrip.com">support&#64;brightscrip.com</a>.
  </p>
  <p>
    Residents of certain states under 13, 16, or 18 years of age may have additional rights regarding the collection and sale of their
    personal information. Please see <a (click)="jumpToElement('state-privacy-rights')">Your State Privacy Rights</a> for more information.
  </p>
  <h2>Information We Collect and How We Collect It</h2>
  <p>We collect several types of information from and about users of our Contact Center and Websites, including information:</p>
  <ul>
    <li>
      By which you may be personally identified, such as name, postal address, email address, telephone number, any other identifier by
      which you may be contacted online or offline (“<b>personal information</b>”);
    </li>
    <li>That is about you but individually does not identify you, such as times and dates of contact and visits to the site;</li>
    <li>Occupation/title, other professional and employment information; and/or</li>
    <li>
      User’s browser type, user’s operating system, device identification number/serial number (such as for mobile phone, tablet, or
      computer), device/user activity, marketing preference such as text message or email notification and frequency, IP address,
      Geo-location/GPS information.
    </li>
  </ul>
  <p>We collect this information:</p>
  <ul>
    <li>Directly from you when you provide it to us.</li>
    <li>
      Automatically as you navigate through the site. Information collected automatically may include usage details, IP addresses, and
      information collected through cookies and other tracking technologies.
    </li>
  </ul>
  <h3>Information You Provide to Us</h3>
  <p>The information we collect may include:</p>
  <ul>
    <li>Information that you provide by filling in forms. We may also ask you for information if and when you report a problem.</li>
    <li>Records and copies of your correspondence (including names, phone numbers and email addresses), if you contact us.</li>
    <li>Your responses to surveys that we might ask you to complete for research purposes.</li>
  </ul>
  <h3>Information We Collect Through Automatic Data Collection Technologies</h3>
  <p>
    As you interact with our Contact Center and navigate through and with our Websites, we may use automatic data collection technologies to
    collect certain information about your equipment, browsing actions, and patterns, including:
  </p>
  <ul>
    <li>
      Details of your visits to our Websites, including traffic data, location data, logs, and other communication data and the resources
      that you access and use on the Websites.
    </li>
    <li>Information about your computer and internet connection, including your IP address, operating system, and browser type.</li>
  </ul>
  <p>
    The information we collect automatically may include personal information that we may maintain it or associate it with personal
    information we collect in other ways or receive from third parties. It helps us to improve our services and to deliver a better and more
    personalized service, including by enabling us to:
  </p>
  <ul>
    <li>Estimate our audience size and usage patterns.</li>
    <li>Store information about your preferences, allowing us to customize our services according to your individual interests.</li>
    <li>Recognize you when you return.</li>
  </ul>
  <p>The technologies we use for this automatic data collection may include:</p>
  <ul>
    <li>
      <b>Cookies (or browser cookies).</b> A cookie is a small file placed on the hard drive of your computer. You may refuse to accept
      browser cookies by activating the appropriate setting on your browser. However, if you select this setting you may be unable to access
      certain parts of our Websites. Unless you have adjusted your browser setting so that it will refuse cookies, our system will issue
      cookies when you direct your browser to our Websites. For information about managing your privacy and security settings for cookies,
      see <a (click)="jumpToElement('choices')">Choices About How We Use and Disclose Your Information</a>.
    </li>
    <li>
      <b>Web Beacons.</b> Pages of our Websites may contain small electronic files known as web beacons (also referred to as clear gifs,
      pixel tags, and single-pixel gifs) that permit the Company, for example, to count users who have visited those pages and for other
      related website statistics (for example, recording the popularity of certain website content and verifying system and server
      integrity).
    </li>
  </ul>
  <h2>How We Use Your Information</h2>
  <p>We use information that we collect about you or that you provide to us, including any personal information:</p>
  <ul>
    <li>To present our Websites and its contents to you.</li>
    <li>To provide you with information, products, or services that you request from us.</li>
    <li>To fulfill any other purpose for which you provide it.</li>
    <li>To perform diagnostic services such as error reporting in order to improve the site or service.</li>
    <li>To perform data analysis and audits.</li>
    <li>
      To track a user’s movement through our platforms. We may receive reports based on the use of tracking technologies on an individual as
      well as aggregated basis.
    </li>
    <li>
      To carry out our obligations and enforce our rights arising from any contracts entered into between you and us, including for billing
      and collection.
    </li>
    <li>To notify you about changes to our Websites or any products or services we offer or provide though it.</li>
    <li>In any other way we may describe when you provide the information.</li>
    <li>For any other purpose with your consent.</li>
  </ul>
  <h2>Disclosure of Your Information</h2>
  <p>
    We may disclose aggregated information about our users, and information that does not identify any individual, without restriction and
    in accordance with all applicable laws.
  </p>
  <p>We may disclose personal information that we collect or you provide as described in this privacy policy:</p>
  <ul>
    <li>To our subsidiaries and affiliates.</li>
    <li>To contractors, service providers, and other third parties we use to support our business.</li>
    <li>
      To a buyer or other successor in the event of a merger, divestiture, restructuring, reorganization, dissolution, or other sale or
      transfer of some or all assets, whether as a going concern or as part of bankruptcy, liquidation, or similar proceeding, in which
      personal information about our Website users is among the assets transferred.
    </li>
    <li>To fulfill the purpose for which you provide it.</li>
    <li>For any other purpose disclosed by us when you provide the information.</li>
    <li>With your consent.</li>
    <li>To our clients in connection with the applicable patient savings programs.</li>
  </ul>
  <p>We may also disclose your personal information:</p>
  <ul>
    <li>To comply with any court order, law, or legal process, including to respond to any government or regulatory request.</li>
    <li>
      To enforce or apply our <a href="terms-of-use">Terms of Use</a> and other agreements, including for billing and collection purposes.
    </li>
    <li>
      If we believe disclosure is necessary or appropriate to protect the rights, property, or safety of Monarch Specialty Group LLC, our
      clients, or others. This includes exchanging information with other companies and organizations for the purposes of fraud protection
      and credit risk reduction.
    </li>
  </ul>
  <h2 id="choices">Choices About How We Use and Disclose Your Information</h2>
  <p>
    We strive to provide you with choices regarding the personal information you provide to us. We have created mechanisms to provide you
    with the following control over your information:
  </p>
  <ul>
    <li>
      <b>Tracking Technologies.</b> You can set your browser to refuse all or some browser cookies, or to alert you when cookies are being
      sent. If you disable or refuse cookies, please note that some parts of this site may then be inaccessible or not function properly.
    </li>
  </ul>
  <p>
    Residents of certain states may have additional personal information rights and choices. Please see
    <a (click)="jumpToElement('state-privacy-rights')">Your State Privacy Rights</a> for more information.
  </p>
  <h2>Accessing and Correcting Your Information</h2>
  <p>
    If applicable, you can review and change your personal information by logging into brightscrip.com and visiting your account profile
    page.
  </p>
  <p>
    You may also send us an email at <a href="mailto:support@brightscrip.com">support&#64;brightscrip.com</a> to request access to, correct,
    or delete any personal information that you have provided to us. We may not accommodate a request to change information if we believe
    the change would violate any law or legal requirement or cause the information to be incorrect.
  </p>
  <p>
    Residents of certain states may have additional personal information rights and choices. Please see
    <a (click)="jumpToElement('state-privacy-rights')">Your State Privacy Rights</a> for more information.
  </p>
  <h2>Health Insurance Portability and Accountability Act ("HIPAA")</h2>
  <p>
    As a key provider of services and technology to the healthcare industry, Monarch Specialty Group LLC has implemented programs to address
    the privacy and security requirements of HIPAA.
  </p>
  <h2 id="state-privacy-rights">Your State Privacy Rights</h2>
  <p>State consumer privacy laws may provide their residents with additional rights regarding our use of their personal information.</p>
  <p>
    California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Montana, Oregon, Tennessee, Texas, Utah, and Virginia provide (now
    or in the future) their state residents with rights to:
  </p>
  <ul>
    <li>Confirm whether we process their personal information.</li>
    <li>Access and delete certain personal information.</li>
    <li>
      Correct inaccuracies in their personal information, taking into account the information’s nature processing purpose (excluding Iowa
      and Utah).
    </li>
    <li>Data portability.</li>
    <li>Opt-out of personal data processing for:</li>
    <ul>
      <li>targeted advertising (excluding Iowa);</li>
      <li>sales; or</li>
      <li>profiling in furtherance of decisions that produce legal or similarly significant effects (excluding Iowa and Utah).</li>
    </ul>
    <li>Either limit (opt-out of) or require consent to process sensitive personal data.</li>
  </ul>
  <p>The exact scope of these rights may vary by state. To exercise any of these rights please email brightscrip.com.</p>
  <h2>Data Security</h2>
  <p>
    We have implemented measures designed to secure your personal information from accidental loss and from unauthorized access, use,
    alteration, and disclosure. All information you provide to us is stored on our secure servers behind firewalls. Any payment transactions
    and other sensitive information will be encrypted.
  </p>
  <p>
    The safety and security of your information also depends on you. Where we have given you (or where you have chosen) a password for
    access to certain parts of our Websites, you are responsible for keeping this password confidential. We ask you not to share your
    password with anyone.
  </p>
  <p>
    Unfortunately, the transmission of information via the internet is not completely secure. Although we do our best to protect your
    personal information, we cannot guarantee the security of your personal information transmitted to our Website. Any transmission of
    personal information is at your own risk. We are not responsible for circumvention of any privacy settings or security measures
    contained on the Website.
  </p>
  <h2 id="changes-to-our-privacy-policy">Changes to Our Privacy Policy</h2>
  <p>
    It is our policy to post any changes we make to our privacy policy on this page. If we make material changes to how we treat our users’
    personal information, we will notify you through a notice on the Website home page. The date the privacy policy was last revised is
    identified at the top of the page. You are responsible for ensuring we have an up-to-date active and deliverable email address for you,
    and for periodically visiting our Website and this privacy policy to check for any changes.
  </p>
  <h2>Contact Information</h2>
  <p>To ask questions or comment about this privacy policy and our privacy practices, contact us at:</p>
  <p>
    <a href="mailto:support@brightscrip.com">support&#64;brightscrip.com</a>
  </p>
  <p>or via phone:</p>
  <p>
    <a href="tel:833-613-2333">833-613-2333</a>
  </p>
</app-legal-layout>
